import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useCopy } from "../../../shared/i18n";
import { AlertIcon, CheckIcon } from "../../../shared/ui/icons";
import { AuthShell } from "../../../app/layouts/AuthShell";
import { BackButton } from "../../../shared/components";
import {
  getPublicPlans,
  formatPaise,
  type PlanRow,
} from "../../../shared/api/billing";
import {
  claimSchoolSignup,
  requestSchoolSignup,
} from "../../../shared/api/signup";
import { canRequestSignup, planListState, seatCount } from "./signupModel";
import { passwordChecks, MIN_PASSWORD_LENGTH } from "./inviteModel";
import { IconArrowRight } from "../../../shared/console";
import { AuthHeading, AuthSplit, type AuthPoint } from "../../console/public/AuthSplit";
import { FormAlert } from "../../console/public/fields";
import { IconCheckCircle, IconKey } from "../../console/public/icons";
import {
  LockedPasswordInput,
  PasswordChecklist,
  Stepper,
  type StepDef,
} from "../../console/public/SetPasswordParts";
import { LABEL, SUBMIT_FULL } from "../../console/public/styles";

/**
 * A school signing itself up, in two screens and one email.
 *
 * 🔴 §3 SAYS *"A SCHOOL CANNOT MINT ITS OWN FIRST ADMIN"*, AND NEITHER SCREEN
 * DOES. The form asks the server for a link and nothing is created; the school,
 * its first ADMIN and its subscription are written when the link comes back.
 * The operator's provisioning path is untouched and remains how a school is
 * onboarded by hand — this is the self-service door beside it, not a
 * replacement.
 *
 * 🔴 THE NUMBER OF PLACES IS ASKED FOR HERE AND NOWHERE ELSE. It becomes
 * `Subscription.seatLimit`: a ceiling on how many children the school may
 * enrol, never the figure they are billed on (`seats.ts`). So the copy says
 * "places" rather than "seats" — a school reading "seats" beside a price
 * reasonably concludes it is what they are paying for, and it is not.
 *
 * ⚠️ THE LINK CANNOT BE EMAILED YET. `invite.prisma` records the gap — *"there
 * is no email sender in this repo… the transport was a human with a chat
 * window"* — so the server returns it only under `SIGNUP_DEV_ECHO`, and this
 * screen shows it when it is there. That is a development affordance with the
 * flag's name attached, not a feature: the day a mailer lands, the block simply
 * stops rendering.
 */

const FIELD =
  "mt-1.5 h-12 w-full rounded-lg border border-line-strong bg-surface px-3.5 text-dash-md text-ink outline-none transition-colors duration-150 ease-dash focus:border-primary";

const SUBMIT =
  "mt-7 h-12 w-full rounded-pill bg-primary text-dash-md font-bold text-primary-ink transition-colors duration-150 ease-dash enabled:hover:bg-primary-deep disabled:bg-line-strong disabled:text-ink-soft";

function Wordmark() {
  return (
    <img
      src="/kalqy-wordmark.png"
      alt="Kalqy"
      width={2000}
      height={585}
      className="h-9 w-auto"
    />
  );
}

/** ⚠️ Icon + colour + text, never colour alone (design-system.md §2). */
function Alert({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p
      role="alert"
      data-testid={testId}
      className="mt-5 flex items-start gap-2 rounded-lg border border-pill-danger-line bg-pill-danger-bg px-3 py-2.5 text-dash-sm font-bold text-pill-danger-fg"
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0">
        <AlertIcon size={16} strokeWidth={2.4} />
      </span>
      {children}
    </p>
  );
}

/* ──────────────────────────────────── step one · the form ── */

export function SchoolSignUpScreen({ onSignIn }: { onSignIn?: () => void }) {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  /*
   * 🔴 EMPTY AND UNREACHABLE ARE ONE STATE TO THIS SCREEN, AND BOTH MUST BE SAID
   * OUT LOUD. `publicPlans()` filters on `isActive AND isPublic`, and the
   * operator's create-plan form defaults `isPublic` to off — so "no price list"
   * is where a real deployment sits until somebody ticks a box, not an exotic
   * failure. What it must never be is silent: this screen rendered a "Price on
   * request" option worth the empty string, which the submit gate below refuses
   * and `schoolSignupSchema` would refuse after it — a filled-in form under a
   * dead button that explained nothing, which is what a school reads as broken.
   */
  const [plansState, setPlansState] = useState<
    "loading" | "ready" | "unavailable"
  >("loading");
  const [schoolName, setSchoolName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [planCode, setPlanCode] = useState("");
  const [seats, setSeats] = useState("");
  const [state, setState] = useState<
    "idle" | "sending" | "sent" | "throttled" | "failed"
  >("idle");
  const [problem, setProblem] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  const t = useCopy({
    en: {
      title: "Create a school account",
      subtitle:
        "Tell us about your school. We will send a link to finish setting it up.",
      school: "School name",
      admin: "Your name",
      email: "Your email",
      plan: "Plan",
      seats: "How many students?",
      /*
       * 🔴 IT SAYS WHAT THE NUMBER DOES, because a school that guesses low finds
       * out by being refused mid-import in September. "You can add more later"
       * would be a promise this product does not keep — raising it is an
       * operator's act.
       */
      seatsHint:
        "This is the number of places on your plan. You will not be able to add more students than this — ask us to change it if your school grows.",
      submit: "Send me the link",
      sending: "Sending…",
      sentTitle: "Check your email",
      sentBody:
        "If that address can be used, a link is on its way. It works once and lasts three days.",
      devLink:
        "Development build — the link is below because no email was sent:",
      throttled: "Too many requests from here. Wait a minute and try again.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      haveAccount: "Already have an account? Sign in",
      note: "We will not charge anything yet. An operator gets in touch before your school goes live.",
      back: "Back",
      free: "Price on request",
      plansLoading: "Loading plans",
      /*
       * ⚠️ IT NAMES THE NEXT ACT, NOT THE FAULT. "Could not load plans" tells a
       * head teacher nothing they can do about it, and this product onboards
       * schools through an operator anyway — #/connect is a form one reads.
       */
      noPlans:
        "There is no published price list yet, so this form cannot finish setting up your school. Tell us about your school and we will get in touch.",
      noPlansCta: "Get in touch",
      perStudent: (amount: string) => `${amount} per student`,
    },
  });

  /*
   * ⚠️ THE PLANS ARE THE PUBLIC PRICE LIST, AND THE FORM SENDS A `code` FROM IT.
   * An id in a public body would be a value somebody could try changing to reach
   * a bespoke district rate that `isPublic: false` keeps off this list.
   */
  useEffect(() => {
    const ac = new AbortController();
    void getPublicPlans(ac.signal)
      .then((rows) => {
        setPlans(rows);
        setPlanCode((current) => current || (rows[0]?.code ?? ""));
        setPlansState(planListState(rows));
      })
      .catch(() => {
        /* ⚠️ THE UNMOUNT ABORT LANDS HERE TOO, and it is not a failure — writing
           state on it warns in development and describes a screen nobody is
           reading. Everything else IS one, and it is now reported rather than
           swallowed: the server is the authority on which plans are public, so
           a caller that could not reach it has no plan it may send. */
        if (ac.signal.aborted) return;
        setPlansState("unavailable");
      });
    return () => {
      ac.abort();
    };
  }, []);

  /* 🔴 THE GATE LIVES IN `signupModel.ts`, WHERE A TEST CAN REACH IT. Written
     inline it was unreachable (OD-60 — no DOM renderer), and it is the rule that
     decides whether the only self-service door into this product opens. */
  const places = seatCount(seats);
  const ready = canRequestSignup({
    schoolName,
    adminName,
    adminEmail,
    planCode,
    seats,
  });

  async function submit(): Promise<void> {
    /* `places` is non-null whenever `ready` is, and it is re-checked rather
       than asserted: a non-null assertion here would be a runtime crash the day
       the two drift apart. */
    if (!ready || places === null) return;
    setState("sending");
    setProblem(null);
    try {
      const result = await requestSchoolSignup({
        schoolName: schoolName.trim(),
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim(),
        planCode,
        seats: places,
      });
      setDevLink(
        result.token === null
          ? null
          : `${window.location.origin}/#/signup/${result.token}`,
      );
      setState("sent");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 429) setState("throttled");
      else if (status === 400) {
        /* The one refusal that is about what was typed — a plan that is not on
           the list, or a places count out of range. The server's sentence names
           which, so it is shown rather than flattened. */
        setProblem(
          err instanceof Error && err.message !== "" ? err.message : null,
        );
        setState("failed");
      } else setState("failed");
    }
  }

  if (state === "sent") {
    return (
      <AuthShell
        nav={<Nav t={t} />}
        brand={<Wordmark />}
        title={t.sentTitle}
        subtitle={t.sentBody}
        footer={t.note}
      >
        <p className="flex items-start gap-2 text-dash-sm text-ink-soft">
          <span aria-hidden="true" className="mt-0.5 shrink-0 text-success">
            <CheckIcon size={16} strokeWidth={2.4} />
          </span>
          {t.sentBody}
        </p>

        {/*
          ⚠️ DEVELOPMENT ONLY, AND IT SAYS SO IN THE COPY. The server returns
          the link exclusively under `SIGNUP_DEV_ECHO`, which it refuses to boot
          with in production — so this block cannot render for a real school,
          and it disappears on its own the day a mailer exists.
        */}
        {devLink !== null ? (
          <div className="mt-5 rounded-lg border border-line-strong bg-canvas p-3">
            <p className="text-dash-xs font-bold text-muted">{t.devLink}</p>
            <a
              href={devLink}
              data-testid="signup-dev-link"
              className="mt-1 block break-all text-dash-xs font-bold text-primary underline"
            >
              {devLink}
            </a>
          </div>
        ) : null}
      </AuthShell>
    );
  }

  return (
    <AuthShell
      nav={<Nav t={t} />}
      brand={<Wordmark />}
      title={t.title}
      subtitle={t.subtitle}
      footer={t.note}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        noValidate
      >
        <label
          className="block text-dash-xs font-bold text-muted"
          htmlFor="school-name"
        >
          {t.school}
        </label>
        <input
          id="school-name"
          data-testid="signup-school"
          value={schoolName}
          onChange={(e) => {
            setSchoolName(e.target.value);
          }}
          maxLength={80}
          autoComplete="organization"
          className={FIELD}
        />

        <label
          className="mt-5 block text-dash-xs font-bold text-muted"
          htmlFor="admin-name"
        >
          {t.admin}
        </label>
        <input
          id="admin-name"
          value={adminName}
          onChange={(e) => {
            setAdminName(e.target.value);
          }}
          maxLength={80}
          autoComplete="name"
          className={FIELD}
        />

        <label
          className="mt-5 block text-dash-xs font-bold text-muted"
          htmlFor="admin-email"
        >
          {t.email}
        </label>
        <input
          id="admin-email"
          type="email"
          data-testid="signup-email"
          value={adminEmail}
          onChange={(e) => {
            setAdminEmail(e.target.value);
          }}
          maxLength={254}
          autoComplete="email"
          className={FIELD}
        />

        <label
          className="mt-5 block text-dash-xs font-bold text-muted"
          htmlFor="plan"
        >
          {t.plan}
        </label>
        <select
          id="plan"
          data-testid="signup-plan"
          value={planCode}
          onChange={(e) => {
            setPlanCode(e.target.value);
          }}
          /* ⚠️ DISABLED RATHER THAN HOLDING A DEAD OPTION. A select somebody can
             open, read and close is one they reasonably believe they have
             answered — and then the button below is inexplicable. */
          disabled={plansState !== "ready"}
          className={FIELD}
        >
          {plans.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name} ·{" "}
              {t.perStudent(formatPaise(p.perStudentPaise, p.currency))}
            </option>
          ))}
          {plansState === "loading" ? (
            <option value="">{t.plansLoading}</option>
          ) : null}
          {plansState === "unavailable" ? (
            <option value="">{t.free}</option>
          ) : null}
        </select>

        <label
          className="mt-5 block text-dash-xs font-bold text-muted"
          htmlFor="seats"
        >
          {t.seats}
        </label>
        <input
          id="seats"
          data-testid="signup-seats"
          /* ⚠️ `text` with a numeric keypad, not `type="number"` — the spinner
             is a control nobody wants on a count, and a number input silently
             accepts `1e3`. The parse is explicit above. */
          inputMode="numeric"
          value={seats}
          onChange={(e) => {
            setSeats(e.target.value.replace(/\D/g, "").slice(0, 4));
          }}
          className={`${FIELD} tabular`}
        />
        <p className="mt-2 text-dash-xs text-muted">{t.seatsHint}</p>

        <button
          type="submit"
          disabled={!ready || state === "sending"}
          className={SUBMIT}
        >
          {state === "sending" ? t.sending : t.submit}
        </button>

        {plansState === "unavailable" && (
          <Alert testId="signup-no-plans">
            <span>
              {t.noPlans}{" "}
              <a href="#/contact-sales" className="underline underline-offset-2">
                {t.noPlansCta}
              </a>
            </span>
          </Alert>
        )}
        {state === "throttled" && <Alert>{t.throttled}</Alert>}
        {state === "failed" && (
          <Alert testId="signup-failed">{problem ?? t.failed}</Alert>
        )}

        {onSignIn ? (
          <p className="mt-5 text-center">
            <button
              type="button"
              onClick={onSignIn}
              className="rounded-pill px-3 py-1.5 text-dash-xs font-bold text-ink-soft underline decoration-line-strong underline-offset-4 transition-colors duration-150 ease-dash hover:text-ink"
            >
              {t.haveAccount}
            </button>
          </p>
        ) : null}
      </form>
    </AuthShell>
  );
}

/* ─────────────────────────── step two · the link comes back ── */

export function SchoolSignUpClaimScreen({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}) {
  const [password, setPassword] = useState("");
  const [state, setState] = useState<
    "idle" | "saving" | "refused" | "conflict" | "failed" | "done"
  >("idle");
  const [problem, setProblem] = useState<string | null>(null);
  const [school, setSchool] = useState<string | null>(null);

  const t = useCopy({
    en: {
      title: "Create your school",
      subtitle: "Choose a password for your account. This is the last step.",
      password: "Password",
      /* The floor is the staff floor, stated because a refused form that does
         not say the rule is a form somebody fails twice. */
      hint: "At least 10 characters.",
      submit: "Create the school",
      saving: "Creating…",
      doneTitle: "Your school is ready",
      doneBody: (name: string) =>
        `${name} has been created. Sign in to set it up.`,
      signIn: "Go to sign in",
      /* One sentence for unknown, expired and already used — the server does not
         distinguish them, deliberately. */
      refused:
        "That link is not valid. It may have expired, or it may already have been used.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
      back: "Back",
      /* ── the console page (Figma P4's pattern) ── */
      headline: "Welcome to Kalqy",
      stepSent: "Details sent",
      stepSentCaption: "Link opened",
      stepSet: "Set password",
      stepSignIn: "Sign in",
      now: "Now",
      next: "Next",
      done: "Done",
      ruleLength: `At least ${String(MIN_PASSWORD_LENGTH)} characters`,
      ruleNumber: "One number (recommended)",
      ruleCapital: "One capital letter (recommended)",
      expiry: "This link works once and expires 72 hours after it was sent.",
      pointReady: "Your school is almost ready",
      pointReadyBody: "This last step creates the school and its first admin login.",
      pointPassword: "Set a password",
      pointPasswordBody: "Only you can see it. Kalqy never asks for it.",
      pointSignIn: "Then sign in",
      pointSignInBody: "Use the email you gave and this password.",
    },
  });

  const points: readonly AuthPoint[] = [
    { icon: IconCheckCircle, title: t.pointReady, body: t.pointReadyBody },
    { icon: IconKey, title: t.pointPassword, body: t.pointPasswordBody },
    { icon: IconArrowRight, title: t.pointSignIn, body: t.pointSignInBody },
  ];
  const checks = passwordChecks(password);
  const steps: readonly StepDef[] = [
    { title: t.stepSent, caption: t.stepSentCaption, state: "done" },
    {
      title: t.stepSet,
      caption: state === "done" ? t.done : t.now,
      state: state === "done" ? "done" : "current",
    },
    {
      title: t.stepSignIn,
      caption: state === "done" ? t.now : t.next,
      state: state === "done" ? "current" : "next",
    },
  ];

  async function submit(): Promise<void> {
    if (password.length < MIN_PASSWORD_LENGTH) return;
    setState("saving");
    setProblem(null);
    try {
      const result = await claimSchoolSignup(token, password);
      setSchool(result.schoolName);
      setState("done");
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 401 || status === 403) setState("refused");
      else if (status === 409) {
        /*
         * 🔴 THE ONE REFUSAL WORTH SHOWING VERBATIM. A 409 means the school name
         * or the email was taken between the request and now, and the server's
         * sentence says which — "get in touch" and "sign in instead" are
         * different actions, and a generic failure would send somebody to the
         * wrong one.
         */
        setProblem(
          err instanceof Error && err.message !== "" ? err.message : null,
        );
        setState("conflict");
      } else setState("failed");
    }
  }

  if (state === "done") {
    return (
      <AuthSplit headline={t.headline} points={points} testId="signup-claim" width="wide">
        <Stepper steps={steps} />
        <div className="mt-7">
          <AuthHeading
            title={t.doneTitle}
            subtitle={school === null ? undefined : t.doneBody(school)}
          />
        </div>
        <button type="button" onClick={onDone} className={`${SUBMIT_FULL} mt-6`}>
          {t.signIn}
          <IconArrowRight size={16} />
        </button>
      </AuthSplit>
    );
  }

  return (
    <AuthSplit headline={t.headline} points={points} testId="signup-claim" width="wide">
      <Stepper steps={steps} />
      <form
        className="mt-7"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        noValidate
      >
        <AuthHeading title={t.title} subtitle={t.subtitle} />

        <label className={`${LABEL} mt-6`} htmlFor="signup-password">
          {t.password}
        </label>
        <div className="mt-2">
          <LockedPasswordInput
            id="signup-password"
            data-testid="signup-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            autoComplete="new-password"
            maxLength={200}
            aria-describedby="signup-rules"
          />
        </div>
        <PasswordChecklist
          id="signup-rules"
          items={[
            { label: t.ruleLength, met: checks.length },
            { label: t.ruleNumber, met: checks.number },
            { label: t.ruleCapital, met: checks.capital },
          ]}
        />

        <button
          type="submit"
          disabled={password.length < MIN_PASSWORD_LENGTH || state === "saving"}
          aria-busy={state === "saving" || undefined}
          className={`${SUBMIT_FULL} mt-6`}
        >
          {state === "saving" ? null : <IconArrowRight size={16} />}
          {state === "saving" ? t.saving : t.submit}
        </button>

        {state === "refused" && (
          <div className="mt-4">
            <FormAlert testId="signup-refused">{t.refused}</FormAlert>
          </div>
        )}
        {state === "conflict" && (
          <div className="mt-4">
            <FormAlert testId="signup-conflict">{problem ?? t.refused}</FormAlert>
          </div>
        )}
        {state === "failed" && (
          <div className="mt-4">
            <FormAlert>{t.failed}</FormAlert>
          </div>
        )}

        <p className="mt-6 text-[12px] leading-4 text-muted">{t.expiry}</p>
      </form>
    </AuthSplit>
  );
}

/** The way back — A2's chrome, shared by both steps. */
function Nav({ t }: { t: { back: string } }) {
  return (
    <div className="mx-auto flex w-full max-w-[1000px] items-center justify-between">
      <BackButton href="#/start" label={t.back} />
    </div>
  );
}
